import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { Avatar } from "./Avatar.js";

/**
 * Who you are, and everything you can do about it.
 *
 * # It replaces a line of grey text
 *
 * This was `Signed in as Chona · your career · sign out` at 12px in white/40,
 * tucked under a form. It was the only way to reach a career or to sign out, and
 * it was the least visible thing on the page — a person who wanted to change
 * accounts had to go looking for the smallest text on screen.
 *
 * # Closing behaviour is most of the work
 *
 * A menu that only closes when you click its button is a menu that gets stuck
 * open, and on a phone that means it covers the thing you were trying to reach.
 * So it closes on Escape, on a click anywhere outside it, and on choosing
 * anything inside it. All three, because people reach for all three.
 *
 * # It is presentational
 *
 * Props and callbacks only, like everything in `components/`. It does not know
 * what a session is; the page hands it a name and a sign-out function.
 */
export function UserMenu({
  name,
  profileId,
  avatar = null,
  curator = false,
  onSignOut,
}: {
  name: string;
  profileId: string;
  avatar?: string | null;
  /** Whether to offer the curation screen. A hint, never a check — the server
   *  answers 404 to everybody else. */
  curator?: boolean;
  onSignOut: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    // `pointerdown`, not `click`: a click fires after mouseup, so dragging a
    // selection that ends outside the menu would leave it open, and on touch
    // there is a 300ms window where the menu is still covering what was tapped.
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        onClick={() => {
          setOpen((was) => !was);
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-full border border-white/10 bg-stage-900/70 py-1 pl-1 pr-3 backdrop-blur transition hover:border-flash-500/40 hover:bg-stage-800/70"
      >
        <Avatar name={name} profileId={profileId} avatar={avatar} size={32} />
        {/* The name is hidden on a phone, where the avatar alone is the
            control and horizontal room is the scarce thing. */}
        <span className="hidden max-w-32 truncate text-sm font-medium sm:inline">
          {name}
        </span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-2 w-60 overflow-hidden rounded-xl border border-white/10 bg-stage-900/95 shadow-xl backdrop-blur"
        >
          <div className="flex items-center gap-3 border-b border-white/10 p-4">
            <Avatar name={name} profileId={profileId} avatar={avatar} size={40} />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold">{name}</p>
              <p className="text-xs text-white/45">{t("menu.signedIn")}</p>
            </div>
          </div>

          <nav className="p-1">
            <Item to="/settings" onChosen={() => setOpen(false)}>
              {t("menu.settings")}
            </Item>
            {/* Phase 23. Outside a party as well as inside it (the cog) — the
                same page either way, because the argument a rules page settles
                usually happens after the game rather than during it. */}
            {/* Phase 24 above phase 23, deliberately: somebody who does not
                know the game wants the five-step version, and somebody settling
                an argument knows to look past it. */}
            <Item to="/how-to-play" onChosen={() => setOpen(false)}>
              {t("menu.howToPlay")}
            </Item>
            <Item to="/rules" onChosen={() => setOpen(false)}>
              {t("menu.rules")}
            </Item>
            <Item to="/me" onChosen={() => setOpen(false)}>
              {t("menu.career")}
            </Item>
            {curator && (
              <Item to="/admin" onChosen={() => setOpen(false)}>
                {t("menu.curation")}
              </Item>
            )}
          </nav>

          <div className="border-t border-white/10 p-1">
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onSignOut();
              }}
              className="w-full rounded-lg px-3 py-2 text-left text-sm text-white/70 transition hover:bg-white/10 hover:text-white"
            >
              {t("menu.signOut")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** One row of the menu. */
function Item({
  to,
  onChosen,
  children,
}: {
  to: string;
  onChosen: () => void;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      role="menuitem"
      onClick={onChosen}
      className="block rounded-lg px-3 py-2 text-sm text-white/70 transition hover:bg-white/10 hover:text-white"
    >
      {children}
    </Link>
  );
}
